/**
 * OpenSolar Designer Page
 *
 * This page provides access to the OpenSolar 3D designer through the SDK.
 * It allows users to create and edit solar designs for projects.
 */
import { useParams } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LayoutSection } from '@/components/layout/layout-section';
import { EnhancedSdkView } from '@/components/opensolar/enhanced-sdk-view';
import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useOpenSolarSdk } from '@/hooks/use-opensolar-sdk';
import { Save, ChevronLeft, FileEdit } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { cn } from '@/lib/utils';

export default function OpenSolarDesignerPage() {
  const params = useParams();
  const projectId = params.id;
  const { toast } = useToast();
  const [designData, setDesignData] = useState<any>(null);

  // Get project details if we have a project ID
  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: projectId ? ['/api/opensolar/projects', projectId] : ['skip-query'],
    enabled: !!projectId,
  });

  // Use the OpenSolar SDK for advanced interactions
  const {
    sdkRef,
    loaded,
    isIdle,
    isSaving,
    hasDirtyFields,
    handleSdkLoad,
    saveProject,
    fetchDesignData,
  } = useOpenSolarSdk({
    onDesignDataFetched: (data) => {
      setDesignData(data);
      toast({
        title: 'Design data fetched',
        description: 'Solar design data has been successfully retrieved.',
      });
    },
    onQueueProcessed: ({ idle }) => {
      // When the SDK is idle, we can perform operations like fetching design data
      console.log('SDK is idle:', idle);
    },
  });

  // Handler for saving the project
  const handleSave = async () => {
    if (isSaving) return;

    try {
      const success = await saveProject();
      if (success) {
        toast({
          title: 'Project saved',
          description: 'The project has been saved successfully.',
        });
      }
    } catch (error) {
      console.error('Error saving project:', error);
      toast({
        title: 'Save error',
        description: 'There was an error saving the project.',
        variant: 'destructive',
      });
    }
  };

  // Handler for fetching design data
  const handleFetchDesignData = async () => {
    try {
      await fetchDesignData();
    } catch (error) {
      console.error('Error fetching design data:', error);
      toast({
        title: 'Data fetch error',
        description: 'There was an error fetching the design data.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <LayoutSection>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Link to="/opensolar/projects">
              <Button variant="outline" size="icon">
                <ChevronLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold">
                {projectId
                  ? `Edit Solar Design: ${project?.name || projectId}`
                  : 'New Solar Design'}
              </h1>
              {projectId && (
                <p className="text-sm text-muted-foreground">Project ID: {projectId}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleFetchDesignData} disabled={!isIdle || !loaded}>
              <FileEdit className="w-4 h-4 mr-2" />
              Fetch Design Data
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasDirtyFields || isSaving || !isIdle || !loaded}
              className={cn(hasDirtyFields && 'animate-pulse')}
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Design'}
            </Button>
          </div>
        </div>

        {/* SDK Container */}
        <div className="rounded-lg border overflow-hidden bg-card shadow-sm">
          <EnhancedSdkView
            projectId={projectId}
            height={700}
            onDesignDataFetched={setDesignData}
            className="w-full"
          />
        </div>

        {/* Design Data Display (Helpful for debugging) */}
        {designData && (
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Design Data</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted p-4 rounded-md overflow-auto max-h-96 text-xs">
                {JSON.stringify(designData, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}
      </LayoutSection>
    </div>
  );
}
